/**
 * Экран состояния источников данных.
 *
 * Flow: опрос /api/sources раз в 30 секунд → таблица источников →
 *       кнопка внепланового запуска задания → результат виден
 *       на том же экране после ближайшего опроса.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл компонента;
 *   - api.js: listSources, runSourceJob.
 *
 * Связанные файлы:
 *   - src/App.jsx — размещает панель на экране;
 *   - docs/specs/BL-2026-08-15-33d5-source-health.md — техспека;
 *   - docs/specs/BL-2026-08-15-2024-bank-statements.md §5.3 — признак
 *     runnable у задания (кнопка запуска только у запускаемых).
 */

import { useCallback, useEffect, useState } from 'react'

import { listSources, runSourceJob } from './api'

/** Период опроса. Экран смотрят минуту в день — WebSocket тут был бы усложнением. */
const POLL_MS = 30000

const DATA_STATE_TITLES = {
  fresh: 'данные свежие',
  stale: 'данные устарели',
  // «Записей нет» — не свежесть и не устаревание: третье состояние.
  no_data: 'данных нет',
}

const RUN_STATUS_TITLES = {
  ok: 'успешно',
  error: 'ошибка',
  skipped: 'пропущен',
}

const KIND_TITLES = {
  dealing: 'дилинг',
  router: 'маршрутизатор',
  lp_balances: 'остатки провайдеров',
  rates: 'курсы валют',
  derived: 'расчёты',
  manual: 'ручной ввод',
  psp: 'платёжная система',
  bank: 'банк',
}

/** Показывает давность в человекочитаемом виде; null — «неизвестно». */
function formatAge(minutes) {
  if (minutes === null || minutes === undefined) return '—'
  if (minutes < 60) return `${minutes} мин`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours} ч`
  return `${Math.floor(hours / 24)} сут`
}

/** Показывает момент в UTC — время системы едино (LR-005). */
function formatMoment(iso) {
  if (!iso) return '—'
  return new Date(iso).toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
}

/**
 * Строка одного источника.
 *
 * @param {object} source — состояние источника из /api/sources
 * @param {boolean} canRun — есть ли право внепланового запуска
 * @param {Function} onRun — обработчик запуска задания
 */
function SourceRow({ source, canRun, onRun }) {
  const stale = source.data_state !== 'fresh'
  return (
    <div className={`source ${source.is_healthy ? '' : 'source-problem'}`}>
      <div className="source-head">
        <strong>{source.title}</strong>{' '}
        <span className="muted">{KIND_TITLES[source.kind] || source.kind}</span>
        {!source.is_healthy && (
          // Повод называется словами, а не цветом: у источника могут быть
          // одновременно устаревшие данные и упавшее задание, и это разные
          // поводы для разных действий.
          <span className="badge badge-problem">{source.problems.join('; ')}</span>
        )}
      </div>

      <div className="source-data">
        <span className={stale ? 'stale' : ''}>
          {DATA_STATE_TITLES[source.data_state] || source.data_state}
        </span>{' '}
        <span className="muted">
          {formatMoment(source.data_at)} · давность {formatAge(source.data_age_minutes)} ·
          порог {formatAge(source.threshold_minutes)}
        </span>
      </div>

      {source.jobs.length === 0 ? (
        <div className="muted">Прогонов ещё не было.</div>
      ) : (
        <table className="jobs">
          <thead>
            <tr>
              <th>Задание</th>
              <th>Последний прогон</th>
              <th>Исход</th>
              <th>Длительность</th>
              <th>Запуск</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {source.jobs.map((job) => (
              <tr key={job.job} className={job.status === 'error' ? 'row-error' : ''}>
                <td>{job.job}</td>
                <td>{formatMoment(job.finished_at)}</td>
                <td>
                  {RUN_STATUS_TITLES[job.status] || job.status}
                  {job.error && <div className="error-text">{job.error}</div>}
                </td>
                <td>{job.duration_ms} мс</td>
                <td>
                  {job.trigger === 'manual'
                    ? `вручную${job.triggered_by ? ` (${job.triggered_by})` : ''}`
                    : 'по расписанию'}
                </td>
                <td>
                  {/*
                    Кнопка — только у задания, которое есть в реестре
                    коннекторов (job.runnable, техспека BL-2026-08-15-2024
                    §5.3). У загрузки выписок запускать нечего: ручка ответила
                    бы 404, и кнопка обещала бы действие, которого нет.
                    Скрывает кнопку только ЯВНОЕ false: поле в ответе
                    сервера без omitempty, и отсутствие признака означает
                    сервер старше карточки — у такого все задания прежние,
                    запускаемые, и отнимать у них кнопку было бы регрессом.
                  */}
                  {canRun && job.runnable !== false && (
                    <button type="button" onClick={() => onRun(source.code, job.job)}>
                      Запустить
                    </button>
                  )}
                  {job.runnable === false && (
                    <span className="muted cell-note">
                      загрузка вручную — раздел «Банковские выписки»
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * Панель состояния источников.
 *
 * @param {object} user — текущий пользователь; право запуска у admin
 */
export default function SourcesPanel({ user }) {
  const [sources, setSources] = useState(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const canRun = user.role === 'admin'

  const load = useCallback(async () => {
    try {
      const data = await listSources()
      setSources(data.sources)
      setError('')
    } catch (err) {
      // Отказ загрузки не стирает прежнюю картину: устаревшие данные
      // на экране полезнее пустого экрана, если сказано, что они устарели.
      setError(err.message)
    }
  }, [])

  useEffect(() => {
    load()
    const timer = setInterval(load, POLL_MS)
    return () => clearInterval(timer)
  }, [load])

  async function run(code, job) {
    setNotice('')
    try {
      await runSourceJob(code, job)
      // Само сообщение уже на экране; в журнал браузера пишем только отказы
      // (правило 04: критические действия и ошибки загрузки).
      setNotice(`Запущено: ${code}/${job}. Результат появится после прогона.`)
    } catch (err) {
      setNotice(`Не запущено: ${err.message}`)
      console.error('внеплановый запуск не удался', code, job, err)
    }
  }

  if (sources === null && !error) return <p>Загрузка состояния источников…</p>

  const unhealthy = (sources || []).filter((s) => !s.is_healthy).length

  return (
    <section className="sources">
      <h2>
        Источники данных{' '}
        {sources && (
          <span className="muted">
            {unhealthy === 0
              ? `все ${sources.length} в порядке`
              : `${unhealthy} из ${sources.length} требуют внимания`}
          </span>
        )}
      </h2>
      {error && <p className="error">Состояние не обновилось: {error}</p>}
      {notice && <p className="notice">{notice}</p>}
      {(sources || []).map((source) => (
        <SourceRow key={source.code} source={source} canRun={canRun} onRun={run} />
      ))}
    </section>
  )
}
